import { parseChord } from '@/lib/pianoChords';

// Standard bass tuning (low to high): E1, A1, D2, G2 = semitones 4, 9, 14, 19
// Display 5 frets, root note highlighted, fifth (perfect) as secondary marker.
const OPEN_STRING_SEMITONES = [4, 9, 14, 19]; // E, A, D, G
const FRETS = 5;

export default function BassChord({ name, size = 92 }) {
  const parsed = parseChord(name);
  if (!parsed) {
    return (
      <div className="inline-flex flex-col items-center" style={{ width: size }}>
        <div className="w-full aspect-[3/5] rounded-lg border border-dashed border-stone-700 flex items-center justify-center text-stone-500 text-xs">?</div>
        <div className="text-xs font-medium text-stone-300 mt-1">{name}</div>
      </div>
    );
  }
  const rootPc = parsed.rootIndex; // 0..11
  const fifthPc = (rootPc + 7) % 12;

  const w = size;
  const h = size * 1.35;
  const padX = size * 0.12;
  const padTop = size * 0.18;
  const padBot = size * 0.05;
  const gridW = w - padX * 2;
  const gridH = h - padTop - padBot;
  const stringSpacing = gridW / 3; // 4 strings → 3 spacings
  const fretHeight = gridH / FRETS;

  // For each (string, fret) find if this is the root or fifth pitch class.
  const dots = [];
  for (let s = 0; s < 4; s++) {
    for (let f = 1; f <= FRETS; f++) { // fret 1..5, skip open for compactness
      const pc = (OPEN_STRING_SEMITONES[s] + f) % 12;
      const label = pc === rootPc ? 'R' : pc === fifthPc ? '5' : null;
      if (label) {
        dots.push({
          s, f, label,
          isRoot: pc === rootPc,
        });
      }
    }
  }

  return (
    <div className="inline-flex flex-col items-center" style={{ width: w }} data-testid={`bass-chord-${name}`}>
      <svg viewBox={`0 0 ${w} ${h}`} width={w} height={h} className="block">
        {/* Fretboard bg */}
        <rect x={padX} y={padTop} width={gridW} height={gridH} fill="#292524" rx={2} />
        {/* Nut (thick top line) */}
        <line x1={padX} y1={padTop} x2={padX + gridW} y2={padTop} stroke="#78716c" strokeWidth={3} />
        {/* Fret lines */}
        {Array.from({ length: FRETS }).map((_, i) => (
          <line
            key={i}
            x1={padX}
            y1={padTop + (i + 1) * fretHeight}
            x2={padX + gridW}
            y2={padTop + (i + 1) * fretHeight}
            stroke="#57534e"
            strokeWidth={1}
          />
        ))}
        {/* Strings (vertical) */}
        {Array.from({ length: 4 }).map((_, i) => (
          <line
            key={i}
            x1={padX + i * stringSpacing}
            y1={padTop}
            x2={padX + i * stringSpacing}
            y2={padTop + gridH}
            stroke="#d6d3d1"
            strokeWidth={i < 2 ? 1.6 : 1}
          />
        ))}
        {/* Fret markers (double dot on fret 3, single on 5) */}
        {[3, 5].map(f => (
          <circle
            key={f}
            cx={padX + gridW / 2}
            cy={padTop + (f - 0.5) * fretHeight}
            r={2}
            fill="#57534e"
          />
        ))}
        {/* Dots */}
        {dots.map((d, i) => (
          <g key={i}>
            <circle
              cx={padX + d.s * stringSpacing}
              cy={padTop + (d.f - 0.5) * fretHeight}
              r={size * 0.09}
              fill={d.isRoot ? '#c2410c' : '#78716c'}
              stroke="#0c0a09"
              strokeWidth={1}
            />
            <text
              x={padX + d.s * stringSpacing}
              y={padTop + (d.f - 0.5) * fretHeight + size * 0.03}
              textAnchor="middle"
              fontSize={size * 0.09}
              fill="#fafaf9"
              fontWeight="700"
            >{d.label}</text>
          </g>
        ))}
        {/* String labels below */}
        {['E', 'A', 'D', 'G'].map((str, i) => (
          <text
            key={i}
            x={padX + i * stringSpacing}
            y={h - 2}
            textAnchor="middle"
            fontSize={size * 0.09}
            fill="#78716c"
          >{str}</text>
        ))}
      </svg>
      <div className="text-xs font-display text-stone-100 mt-1">{name}</div>
    </div>
  );
}
